import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { marked } from "marked";
import axios from "axios";
import { useI18n } from "../I18nContext";
import { useAuth } from "../AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

marked.setOptions({ gfm: true, breaks: true });

function DocMeta({ doc, t }) {
  if (!doc) return null;
  const fullTitle = doc.title && !/^tractionboard/i.test(doc.title)
    ? `TractionBoard ${doc.title}`
    : doc.title;
  return (
    <div className="tb-legal-body-meta-plain" data-testid={`legal-meta-${doc.slug}`}>
      <h1 className="tb-legal-body-doc-title">{fullTitle}</h1>
      <div>{t("legal.versionLabel")}: {doc.version}</div>
      {doc.effective_date && (
        <div>{t("legal.effectiveDate")}: {doc.effective_date}</div>
      )}
      {doc.last_updated && (
        <div>{t("legal.updated")}: {doc.last_updated}</div>
      )}
      {doc.jurisdiction && (
        <div>{t("legal.jurisdiction")}: {doc.jurisdiction}</div>
      )}
    </div>
  );
}

function LegalShell({ children, inApp }) {
  if (inApp) {
    return <div className="tb-page tb-legal-inapp">{children}</div>;
  }
  return (
    <div className="tb-app tb-legal-page">
      <header className="tb-app-bar tb-legal-app-bar">
        <Link to="/" className="tb-app-logo" data-testid="legal-public-logo">
          Traction<span>Board</span>
        </Link>
      </header>
      <div className="tb-page">{children}</div>
      <footer className="tb-app-footer" data-testid="app-footer">
        <span className="tb-muted"><small>TractionBoard v0.2.4</small></span>
      </footer>
    </div>
  );
}

export function LegalIndex({ inApp = false }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/api/legal`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  // Authed users hitting the public /legal → redirect into the app so they get the nav.
  // Wait for auth to resolve (user === null while loading, undefined not possible here).
  if (user && !inApp) return <Navigate to="/app/legal" replace />;
  const legalBase = inApp ? "/app/legal" : "/legal";

  return (
    <LegalShell inApp={inApp}>
      <header className="tb-page-title-header">
        <h1>{t("legal.centerTitle")}</h1>
        <p className="tb-muted">{t("legal.centerSubtitle")}</p>
      </header>
      {err && <div className="tb-error">{err}</div>}
      {loading ? (
        <p className="tb-muted">{t("legal.loading")}</p>
      ) : (
        <ul className="tb-legal-list" data-testid="legal-list">
          {items.map((it) => (
            <li key={it.slug} data-testid={`legal-item-${it.slug}`}>
              <Link to={`${legalBase}/${it.slug}`} className="tb-legal-link">
                <strong>{it.title}</strong>
              </Link>
              {it.required_for_signup && (
                <span className="tb-badge good">{t("legal.required")}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </LegalShell>
  );
}

export function LegalViewer({ inApp = false }) {
  const { slug } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE}/api/legal/${slug}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [slug]);

  const html = useMemo(() => (doc ? marked.parse(doc.body_md || "") : ""), [doc]);

  if (user && !inApp) return <Navigate to={`/app/legal/${slug}`} replace />;
  const backTo = inApp ? "/app/legal" : "/legal";

  return (
    <LegalShell inApp={inApp}>
      <div style={{ marginBottom: 12 }}>
        <Link to={backTo} className="tb-link" data-testid="legal-back">
          ← {t("legal.backToCenter")}
        </Link>
      </div>
      {loading && <p className="tb-muted">{t("legal.loading")}</p>}
      {err && <div className="tb-error">{err}</div>}
      {doc && (
        <>
          <header className="tb-page-title-header">
            <h1 data-testid="legal-title">{doc.title}</h1>
            <p className="tb-muted">{t("legal.centerSubtitle")}</p>
          </header>
          <article className="tb-legal-body" data-testid={`legal-body-${slug}`}>
            <div className="tb-legal-body-meta">
              <DocMeta doc={doc} t={t} />
            </div>
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </article>
        </>
      )}
    </LegalShell>
  );
}
